import { useEffect, useRef, useState } from 'react';
import type { ReadAloud } from './useReadAloud';
import type { Dictation, NothingHeard } from './useDictation';

/**
 * THE BACK-AND-FORTH MODE (VOICE1, size A, slice 3 - the 132nd, 2026-10-11).
 *
 * His words, after his own iPhone tried slices 1 and 2: "it worked, and I had to push send, right? ... it doesn't work
 * like, you know, OpenAI or, or Claude, the voice chat." His yes: "let's build step two". So: ONE tap, he talks, his
 * words are SENT when he stops, the answer is read aloud, and the phone listens again. The same chat, the same brain,
 * the phone's own ears and voice - no service, no cost beyond the chat turns themselves.
 *
 * This hook only DECIDES. The ears are `useDictation`, the voice is `useReadAloud`, and sending is the panel's `send`.
 *
 * ⚠️⚠️ THE ONE FACT NOBODY HAS MEASURED: whether an iPhone will start LISTENING when the app asks it to, outside a tap.
 *    Measuring it needs a microphone ALLOWED - his phone. So this is built to work EITHER way:
 *      - the phone listens by itself: the conversation runs with no tap between turns;
 *      - it will not (it refuses, it ends without starting, or it is still waiting after 4 seconds): the panel says
 *        "Tap the microphone to answer." and the conversation goes on with ONE tap a turn. ⛔ Never a dead screen.
 * ⭐⭐ WHAT IT STANDS ON, ALL MEASURED (`docs/planning/2026-10-10-voice1-probe/README.md`, both sections):
 *      - the first sound must start inside a tap - so the tap that turns this on says one SILENT word (the probe's
 *        speech events were all measured at volume 0) and asks the phone to listen, in that order, in that tap;
 *      - the voice is stopped BEFORE the phone listens, every time;
 *      - a refused microphone is an ERROR with no `end` after it, and a second try in the same run of the app is
 *        refused without asking - so a refusal of HIS tap ends the mode, in the sentence that says how to be asked again;
 *      - a refusal of the APP's own try is not his to fix: the device's reason is taken off the screen (`forget`),
 *        because "not allowed - close the app" would send him to mend a permission that is not broken.
 * ⛔ NEVER REMEMBERED. In this mode his words go out WITHOUT HIS READING THEM; he turns it on knowingly, each time.
 * ⛔ "THE VOICE HAS FINISHED" IS NOT "THE ANSWER HAS FINISHED": a paragraph is read as it lands, so the voice can catch
 *    up while the rest is still arriving. Listening is owed only once the panel says the turn has ended.
 * ⛔ It stops listening by itself: on silence · when an answer opened something to be filled by hand · when he leaves
 *    (the chat hidden behind the other tab, or the page itself put away). New chat and Read aloud off END it.
 */

export const TAP_TO_ANSWER = 'Tap the microphone to answer.';
export const NOTHING_HEARD = 'Nothing was heard. Tap the microphone to answer.';

/** How long the phone has to BEGIN listening when the app, not his finger, asked it to. */
const MUST_LISTEN_WITHIN_MS = 4000;
/** The phone's "no" to the microphone itself. Measured: no second tap changes it in this run of the app. */
const REFUSED = new Set(['not-allowed', 'service-not-allowed', 'audio-capture']);
/** The device's codes for plain silence - nothing is wrong, nothing was said. */
const SILENCE = new Set<string | null>([null, 'no-speech']);

export interface VoiceChat {
  /** This browser can both listen and speak. False hides the control. */
  supported: boolean;
  on: boolean;
  /** Why it is not listening by itself right now, in a sentence. Null while a turn is under way. */
  paused: string | null;
  /** ⛔ CALL INSIDE THE TAP: the silent word and the listening both start in it. */
  turnOn: () => void;
  turnOff: () => void;
  /** ⛔ CALL INSIDE THE TAP on the microphone. Finishes a listen that is under way; otherwise cuts the voice and listens. */
  micTapped: () => void;
  /** A message is going out, spoken or typed. */
  turnBegan: () => void;
  /** Its answer has landed and been handed to the voice. `listenNext` is false when there is something to do by hand. */
  turnEnded: (listenNext: boolean) => void;
  /** From `useDictation`: a listen ended with no words. */
  heardNothing: (why: NothingHeard) => void;
  /** From `useReadAloud`: the voice has said everything it was given. */
  voiceFinished: () => void;
  /** He can no longer see the chat. The microphone and the voice stop; one tap picks it up again. */
  leave: () => void;
}

export const useVoiceChat = (parts: { readAloud: ReadAloud; dictation: Dictation }): VoiceChat => {
  const supported = parts.readAloud.supported && parts.dictation.supported;
  const [on, setOn] = useState(false);
  const [paused, setPaused] = useState<string | null>(null);

  /** The two hooks as they are THIS render - a tap, a timer and a device event all read them long after one. */
  const voice = useRef(parts.readAloud);
  voice.current = parts.readAloud;
  const mic = useRef(parts.dictation);
  mic.current = parts.dictation;

  /** `on` as it is this instant: the tap that turns it on asks the phone to listen before any render. */
  const onNow = useRef(false);
  /** Who asked for the listen now under way: his finger, or the app. */
  const askedBy = useRef<'tap' | 'app'>('tap');
  /** The turn has ended and the voice is still reading: listening is owed when it finishes. */
  const owed = useRef(false);
  /** He left while it was on. Nothing listens by itself until a turn of his own begins. */
  const away = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const disarm = (): void => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  };

  const listen = (by: 'tap' | 'app'): void => {
    disarm();
    askedBy.current = by;
    setPaused(null);
    // ⛔ The measured order: the voice is stopped, THEN the phone listens - it must not hear the app talking.
    voice.current.stop();
    mic.current.start();
    if (by !== 'app') return;
    // His tap may be answered by a prompt he takes his time over. The app's own try has nobody to wait for.
    timer.current = setTimeout(() => {
      timer.current = null;
      if (!onNow.current || mic.current.state !== 'waiting') return;
      mic.current.cancel();
      setPaused(TAP_TO_ANSWER);
    }, MUST_LISTEN_WITHIN_MS);
  };

  const turnOff = (): void => {
    // ⛔ Off already: the panel's own New chat still stops the voice and the microphone itself, exactly as it did.
    if (!onNow.current) return;
    onNow.current = false;
    setOn(false);
    // A try of the app's own may still be counting: it must not come back for a listen his next tap begins.
    disarm();
    // Half-heard words are thrown away: he ended the conversation, he did not finish a sentence.
    mic.current.cancel();
    voice.current.stop();
  };

  const turnOn = (): void => {
    onNow.current = true;
    setOn(true);
    setPaused(null);
    // ⛔ Both inside this tap, in this order: one SILENT word (it unlocks the voice for the answer that arrives
    //   seconds later, and says nothing into the microphone), then the phone is asked to listen.
    voice.current.turnOn(true);
    askedBy.current = 'tap';
    mic.current.start();
  };

  const micTapped = (): void => {
    const state = mic.current.state;
    // He is being listened to, or his own tap is waiting on the phone's prompt: this tap finishes it.
    if (state === 'listening' || (state === 'waiting' && askedBy.current === 'tap')) { mic.current.stop(); return; }
    // The app's own try is still pending. A try made inside this tap is the one a phone will honour.
    if (state === 'waiting') mic.current.cancel();
    listen('tap');
  };

  const turnBegan = (): void => {
    // Whatever was owed for the LAST answer is not owed for this one, and a message of his own means he is here.
    owed.current = false;
    away.current = false;
    setPaused(null);
  };

  const turnEnded = (listenNext: boolean): void => {
    if (!onNow.current) return;
    // Something is open to be filled by hand, or he is not looking: it waits for his tap.
    if (!listenNext || away.current) { setPaused(TAP_TO_ANSWER); return; }
    if (voice.current.busy()) {
      owed.current = true;
      // ⛔ A voice that will not speak never finishes (measured: no start, no end, no error). He is told to tap now.
      if (voice.current.silent) setPaused(TAP_TO_ANSWER);
      return;
    }
    listen('app');
  };

  const voiceFinished = (): void => {
    if (owed.current) listen('app');
  };

  const heardNothing = (why: NothingHeard): void => {
    if (!onNow.current) return;
    // ⚠️ THE UNMEASURED FACT, the other way: the app asked and the phone never began. One tap a turn from here -
    //   and the device's reason for refusing the APP is taken off the screen: it is not his to act on.
    if (askedBy.current === 'app' && !why.began) { mic.current.forget(); setPaused(TAP_TO_ANSWER); return; }
    // ⛔ His own tap was refused. Measured: tapping again cannot change that in this run of the app - so the mode
    //   ENDS, and the device's reason (which says how to be asked again) is what he reads.
    if (why.code !== null && REFUSED.has(why.code)) { turnOff(); return; }
    // ⛔ Silence stops it: a phone left listening to a quiet room is the endless microphone he did not ask for.
    setPaused(why.began && SILENCE.has(why.code) ? NOTHING_HEARD : TAP_TO_ANSWER);
  };

  const leave = (): void => {
    if (!onNow.current) return;
    away.current = true;
    mic.current.cancel();
    voice.current.stop();
    setPaused(TAP_TO_ANSWER);
  };

  // ⛔ The voice went silent on an answer already handed to it: `voiceFinished` may never come. He is told to tap.
  const silent = parts.readAloud.silent;
  useEffect(() => {
    if (silent && onNow.current && owed.current) setPaused(TAP_TO_ANSWER);
  }, [silent]);

  // Read aloud turned off: a conversation cannot go on without the voice.
  const voiceOn = parts.readAloud.on;
  useEffect(() => {
    if (!voiceOn) turnOff();
  }, [voiceOn]); // eslint-disable-line react-hooks/exhaustive-deps

  // The page itself put away - the phone locked, another app in front. Coming back stops nothing.
  const leaveNow = useRef(leave);
  leaveNow.current = leave;
  useEffect(() => {
    const onVisibility = (): void => { if (document.visibilityState === 'hidden') leaveNow.current(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  return { supported, on, paused, turnOn, turnOff, micTapped, turnBegan, turnEnded, heardNothing, voiceFinished, leave };
};
